import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios';
import { withGlobalError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';

export interface KubernetesStackPayload {
  method: string;
  type: string;
  StackName: string;
  ComposeFormat: boolean;
  Namespace: string;
  RepositoryURL: string;
  RepositoryReferenceName: string;
  RepositoryAuthentication: boolean;
  RepositoryUsername: string;
  RepositoryPassword: string;
  RepositoryAuthorizationType: number;
  RepositoryGitCredentialID: number;
  ManifestFile: string;
  HelmChartPath: string;
  AdditionalFiles: string[];
  HelmValuesFiles: string[];
  TLSSkipVerify: boolean;
  AutoUpdate: {
    Interval: string;
    Webhook: string;
    ForceUpdate: boolean;
    ForcePullImage: boolean;
  } | null;
}

export function useCreateKubernetesStackMutation(environmentId: EnvironmentId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: KubernetesStackPayload) =>
      createKubernetesStack(environmentId, payload),
    onSuccess: () => {
      // Invalidate relevant queries to refetch data
      queryClient.invalidateQueries(['kubernetes', 'stacks', environmentId]);
      queryClient.invalidateQueries([
        'kubernetes',
        'applications',
        environmentId,
      ]);
    },
    ...withGlobalError('Unable to create Kubernetes stack'),
  });
}

async function createKubernetesStack(
  environmentId: EnvironmentId,
  payload: KubernetesStackPayload
) {
  try {
    const { data } = await axios.post(
      `stacks/create/kubernetes/repository`,
      payload,
      {
        params: {
          endpointId: environmentId,
        },
      }
    );
    return data;
  } catch (e) {
    throw parseAxiosError(e, 'Unable to create Kubernetes stack');
  }
}
